import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { 
  MagnifyingGlassIcon, 
  FunnelIcon, 
  ArrowsUpDownIcon, 
  PencilIcon, 
  TrashIcon,
  ArrowDownTrayIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import api from '../../utils/api';

const InventoryPage = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    listId: '',
    category: '',
    status: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20
  });
  const [sort, setSort] = useState({
    sortBy: 'createdAt',
    sortOrder: 'DESC'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  
  // Fetch inventory items
  const {
    data: inventoryData,
    isLoading,
    isError,
    error
  } = useQuery(
    ['inventory', searchTerm, filters, pagination, sort],
    async () => {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        sortBy: sort.sortBy,
        sortOrder: sort.sortOrder
      });
      
      if (searchTerm) params.append('search', searchTerm);
      if (filters.listId) params.append('listId', filters.listId);
      if (filters.category) params.append('category', filters.category);
      if (filters.status) params.append('status', filters.status);
      
      const response = await api.get(`/api/inventory?${params}`);
      return response.data;
    },
    {
      keepPreviousData: true
    }
  );
  
  // Fetch lists for filter
  const { data: lists } = useQuery('lists', async () => {
    const response = await api.get('/api/lists/active');
    return response.data;
  });
  
  // Reset selected items when inventory data changes
  useEffect(() => {
    setSelectedItems([]);
  }, [inventoryData]);
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page on search
  };
  
  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page on filter change
  };
  
  // Handle sort change
  const handleSortChange = (field) => {
    setSort(prev => {
      if (prev.sortBy === field) {
        // Toggle order if same field
        return {
          sortBy: field,
          sortOrder: prev.sortOrder === 'ASC' ? 'DESC' : 'ASC'
        };
      } else {
        // Default to DESC for new field
        return {
          sortBy: field,
          sortOrder: 'DESC'
        };
      }
    });
  };
  
  // Handle pagination
  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      page: newPage
    }));
  };
  
  // Handle item selection
  const handleItemSelect = (itemId) => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };
  
  // Handle select all items
  const handleSelectAll = () => {
    if (inventoryData && inventoryData.items) {
      if (selectedItems.length === inventoryData.items.length) {
        setSelectedItems([]);
      } else {
        setSelectedItems(inventoryData.items.map(item => item.id));
      }
    }
  };
  
  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    
    if (window.confirm(`Are you sure you want to delete ${selectedItems.length} item(s)?`)) {
      try {
        // Delete items one by one
        for (const itemId of selectedItems) {
          await api.delete(`/api/inventory/${itemId}`);
        }
        
        // Refresh inventory data
        queryClient.invalidateQueries('inventory');
        setSelectedItems([]);
        
        alert('Items deleted successfully');
      } catch (err) {
        console.error('Failed to delete items:', err);
        alert('Failed to delete items. Please try again.');
      }
    }
  };
  
  // Handle bulk export
  const handleBulkExport = async () => {
    if (selectedItems.length === 0) return;
    
    try {
      // This would normally call the export API
      alert(`Export ${selectedItems.length} items to Excel`);
      // In a real implementation, this would trigger a download
    } catch (err) {
      console.error('Failed to export items:', err);
      alert('Failed to export items. Please try again.');
    }
  };
  
  // Render no items message
  const renderNoItems = () => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan="7" className="py-10 text-center text-gray-500">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
            </div>
            <p className="mt-2">Loading inventory items...</p>
          </td>
        </tr>
      );
    }
    
    if (isError) {
      return (
        <tr>
          <td colSpan="7" className="py-10 text-center text-red-500">
            <p>Error loading inventory: {error?.message || 'Unknown error'}</p>
            <button 
              onClick={() => queryClient.invalidateQueries('inventory')}
              className="mt-2 text-primary-600 hover:text-primary-500"
            >
              Try again
            </button>
          </td>
        </tr>
      );
    }
    
    if (!inventoryData?.items || inventoryData.items.length === 0) {
      return (
        <tr>
          <td colSpan="7" className="py-10 text-center text-gray-500">
            <p>No inventory items found.</p>
            {(searchTerm || filters.listId || filters.category || filters.status) && (
              <p className="mt-2">
                Try adjusting your search or filters.
                <button 
                  onClick={() => {
                    setSearchTerm('');
                    setFilters({ listId: '', category: '', status: '' });
                  }}
                  className="ml-2 text-primary-600 hover:text-primary-500"
                >
                  Clear all filters
                </button>
              </p>
            )}
          </td>
        </tr>
      );
    }
    
    return null;
  };
  
  // Get sort icon for column
  const getSortIcon = (field) => {
    if (sort.sortBy !== field) {
      return (
        <ArrowsUpDownIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
      );
    }
    
    return sort.sortOrder === 'ASC' ? (
      <span className="text-primary-600">↑</span>
    ) : (
      <span className="text-primary-600">↓</span>
    );
  };
  
  // Render status badge
  const renderStatusBadge = (status) => {
    const statusStyles = {
      'AVAILABLE': 'bg-green-100 text-green-800',
      'RESERVED': 'bg-yellow-100 text-yellow-800',
      'SOLD': 'bg-blue-100 text-blue-800',
      'ARCHIVED': 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status || 'UNKNOWN'}
      </span>
    );
  };
  
  return (
    <div className="max-w-7xl mx-auto">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-semibold text-gray-900">Inventory</h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage your inventory items, view details, and organize your catalog.
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <Link
            to="/app/scanner"
            className="btn-primary"
          >
            Add New Item
          </Link>
        </div>
      </div>
      
      {/* Search and filter */}
      <div className="mt-6 flex flex-col sm:flex-row justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
          <input
            type="text"
            className="form-input pl-10"
            placeholder="Search inventory..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
        
        <div className="flex space-x-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FunnelIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          
          {selectedItems.length > 0 && (
            <div className="flex space-x-2">
              <button
                type="button"
                className="btn-secondary text-gray-700"
                onClick={handleBulkExport}
              >
                <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                Export ({selectedItems.length})
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleBulkDelete}
              >
                <TrashIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                Delete ({selectedItems.length})
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Filters */}
      {showFilters && (
        <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-3">
          <div>
            <label htmlFor="listId" className="block text-sm font-medium text-gray-700">
              List
            </label>
            <select
              id="listId"
              name="listId"
              className="form-input mt-1"
              value={filters.listId}
              onChange={handleFilterChange}
            >
              <option value="">All Lists</option>
              {lists?.map(list => (
                <option key={list.id} value={list.id}>
                  {list.name} {list.defaultList && '(Default)'}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label htmlFor="category" className="block text-sm font-medium text-gray-700">
              Category
            </label>
            <select
              id="category"
              name="category"
              className="form-input mt-1"
              value={filters.category}
              onChange={handleFilterChange}
            >
              <option value="">All Categories</option>
              {inventoryData?.categories?.map(category => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label htmlFor="status" className="block text-sm font-medium text-gray-700">
              Status
            </label>
            <select
              id="status"
              name="status"
              className="form-input mt-1"
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value="">All Statuses</option>
              <option value="AVAILABLE">Available</option>
              <option value="RESERVED">Reserved</option>
              <option value="SOLD">Sold</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </div>
        </div>
      )}
      
      {/* Inventory List - Card Style */}
      <div className="mt-6 bg-white shadow rounded-lg">
        {/* Sort Controls */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
              checked={inventoryData?.items && selectedItems.length === inventoryData.items.length}
              onChange={handleSelectAll}
            />
            <div className="flex items-center space-x-6">
              <button 
                onClick={() => handleSortChange('title')}
                className="flex items-center text-sm font-medium text-gray-700 hover:text-primary-600"
              >
                <span>Item</span>
                <span className="ml-1">{getSortIcon('title')}</span>
              </button>
              <button 
                onClick={() => handleSortChange('category')}
                className="flex items-center text-sm font-medium text-gray-700 hover:text-primary-600"
              >
                <span>Category</span>
                <span className="ml-1">{getSortIcon('category')}</span>
              </button>
              <button 
                onClick={() => handleSortChange('listId')}
                className="flex items-center text-sm font-medium text-gray-700 hover:text-primary-600"
              >
                <span>List</span>
                <span className="ml-1">{getSortIcon('listId')}</span>
              </button>
              <button 
                onClick={() => handleSortChange('status')}
                className="flex items-center text-sm font-medium text-gray-700 hover:text-primary-600"
              >
                <span>Status</span>
                <span className="ml-1">{getSortIcon('status')}</span>
              </button>
              <button 
                onClick={() => handleSortChange('createdAt')}
                className="flex items-center text-sm font-medium text-gray-700 hover:text-primary-600"
              >
                <span>Added</span>
                <span className="ml-1">{getSortIcon('createdAt')}</span>
              </button>
            </div>
          </div>
        </div>
        
        {/* Loading or No Items State */}
        {isLoading ? (
          <div className="py-10 text-center text-gray-500">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
            </div>
            <p className="mt-2">Loading inventory items...</p>
          </div>
        ) : isError ? (
          <div className="py-10 text-center text-red-500">
            <p>Error loading inventory: {error?.message || 'Unknown error'}</p>
            <button 
              onClick={() => queryClient.invalidateQueries('inventory')}
              className="mt-2 text-primary-600 hover:text-primary-500"
            >
              Try again
            </button>
          </div>
        ) : !inventoryData?.items || inventoryData.items.length === 0 ? (
          <div className="py-10 text-center text-gray-500">
            <p>No inventory items found.</p>
            {(searchTerm || filters.listId || filters.category || filters.status) && (
              <p className="mt-2">
                Try adjusting your search or filters.
                <button 
                  onClick={() => {
                    setSearchTerm('');
                    setFilters({ listId: '', category: '', status: '' });
                  }}
                  className="ml-2 text-primary-600 hover:text-primary-500"
                >
                  Clear all filters
                </button>
              </p>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {inventoryData.items.map((item) => (
              <div key={item.id} className={`py-3 ${selectedItems.includes(item.id) ? 'bg-primary-50' : ''}`}>
                {/* Item Header - always visible */}
                <div className="flex items-center justify-between py-2 px-4 hover:bg-gray-50 rounded-md">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600 mr-3"
                      checked={selectedItems.includes(item.id)}
                      onChange={() => handleItemSelect(item.id)}
                    />
                    
                    <div className="flex items-center space-x-3">
                      {/* Inventory Number Badge */}
                      {item.inventoryNumber && (
                        <div className="flex-shrink-0 bg-blue-50 text-blue-700 font-semibold px-2 py-1 rounded-md text-sm min-w-[50px] text-center">
                          #{item.inventoryNumber}
                        </div>
                      )}
                    
                      <div className="flex-shrink-0">
                        {item.primaryImage ? (
                          <img 
                            src={item.primaryImage.path} 
                            alt={item.title}
                            className="h-12 w-12 object-cover rounded border border-gray-200" 
                          />
                        ) : (
                          <div className="h-12 w-12 bg-gray-100 rounded flex items-center justify-center">
                            <svg className="h-6 w-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                      </div>
                      
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">
                          <Link to={`/app/inventory/${item.id}`} className="hover:text-primary-600">
                            {item.title}
                          </Link>
                        </h3>
                        <p className="text-xs text-gray-500">
                          {item.upc ? `UPC: ${item.upc}` : ''} 
                          {item.category ? (item.upc ? ` • ` : '') + `Category: ${item.category}` : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Status and actions */}
                  <div className="flex items-center space-x-4">
                    <div className="hidden sm:block">
                      {renderStatusBadge(item.status)}
                    </div>
                    <div className="hidden sm:block text-sm text-gray-500">
                      {new Date(item.createdAt).toLocaleDateString()}
                    </div>
                    <div className="flex space-x-2">
                      <Link
                        to={`/app/inventory/${item.id}`}
                        className="p-1.5 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                      >
                        <PencilIcon className="h-4 w-4" aria-hidden="true" />
                      </Link>
                      <button
                        type="button"
                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        onClick={() => {
                          if (window.confirm(`Delete ${item.title}?`)) {
                            api.delete(`/api/inventory/${item.id}`)
                              .then(() => queryClient.invalidateQueries('inventory'))
                              .catch(err => {
                                console.error('Failed to delete item:', err);
                                alert('Failed to delete item');
                              });
                          }
                        }}
                      >
                        <TrashIcon className="h-4 w-4" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Pagination */}
      {inventoryData && inventoryData.totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="btn-secondary"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === inventoryData.totalPages}
              className="btn-secondary"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{inventoryData.items.length > 0 ? ((pagination.page - 1) * pagination.limit) + 1 : 0}</span> to{' '}
                <span className="font-medium">
                  {Math.min(pagination.page * pagination.limit, inventoryData.total)}
                </span>{' '}
                of <span className="font-medium">{inventoryData.total}</span> results
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {/* Page numbers */}
                {[...Array(inventoryData.totalPages).keys()].map((page) => {
                  const pageNumber = page + 1;
                  // Show first page, last page, and 1 pages around current page
                  if (
                    pageNumber === 1 ||
                    pageNumber === inventoryData.totalPages ||
                    Math.abs(pageNumber - pagination.page) <= 1
                  ) {
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${pageNumber === pagination.page ? 'z-10 bg-primary-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600' : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'}`}
                      >
                        {pageNumber}
                      </button>
                    );
                  } else if (
                    pageNumber === 2 && pagination.page > 3 ||
                    pageNumber === inventoryData.totalPages - 1 && pagination.page < inventoryData.totalPages - 2
                  ) {
                    // Show ellipsis
                    return (
                      <span
                        key={pageNumber}
                        className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-inset ring-gray-300 focus:outline-offset-0"
                      >
                        ...
                      </span>
                    );
                  }
                  
                  return null;
                })}
                
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === inventoryData.totalPages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryPage;
